import { computed, inject, nextTick, onBeforeUnmount, provide, ref, watch, type Ref } from 'vue'

const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
interface OverlayEntry { id: symbol; zIndex: number }
const stackKey = Symbol.for('xjlv2.ui.overlayStack')
function overlayStack(): OverlayEntry[] {
  if (typeof document === 'undefined') return []
  const host = document as Document & { [stackKey]?: OverlayEntry[] }
  return host[stackKey] ?? (host[stackKey] = [])
}
function visible(element: HTMLElement) {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (node.hidden || node.inert || style.display === 'none' || style.visibility === 'hidden') return false
  }
  return true
}
export interface OverlayFocusTrapOptions {
  closeOnEscape?: () => void
  escapeEnabled?: () => boolean
  trapFocus?: boolean
  autoFocus?: boolean
  restoreFocus?: boolean
  trigger?: Ref<HTMLElement | null>
  closeOnOutside?: (event: MouseEvent) => void
  closeOnTab?: () => void
  zIndexToken?: string
  zIndex?: () => number | undefined
}

/** One document-level stack is shared by shell and federated copies of the UI package. */
export function useOverlayFocusTrap(open: Readonly<Ref<boolean>>, panel: Ref<HTMLElement | null>, options: OverlayFocusTrapOptions = {}) {
  const id = Symbol('rr-overlay')
  const zIndex = ref<number>()
  const parentOpen = inject<Readonly<Ref<boolean>> | null>('rrOverlayOpen', null)
  provide('rrOverlayOpen', computed(() => open.value && (parentOpen?.value ?? true)))
  let restoreTarget: HTMLElement | null = null
  let bound = false
  const isTopOverlay = () => overlayStack().at(-1)?.id === id
  function focusables() { return Array.from(panel.value?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []).filter(visible) }
  async function focusInitial() {
    await nextTick()
    if (!open.value || !panel.value || !isTopOverlay() || options.autoFocus === false) return
    const elements = focusables()
    const target = elements.find(element => element.matches('[data-autofocus],[autofocus]'))
      ?? elements.find(element => element.dataset.overlayClose !== 'true') ?? panel.value
    target.focus({ preventScroll: true })
  }
  function handleKeydown(event: KeyboardEvent) {
    if (!open.value || !isTopOverlay() || event.defaultPrevented) return
    if (event.key === 'Escape' && options.closeOnEscape && (options.escapeEnabled?.() ?? true)) {
      event.preventDefault()
      event.stopImmediatePropagation()
      options.closeOnEscape()
      return
    }
    if (event.key !== 'Tab') return
    if (options.trapFocus === false) {
      if (options.closeOnTab) { options.trigger?.value?.focus({ preventScroll: true }); options.closeOnTab() }
      return
    }
    if (!panel.value) return
    const elements = focusables()
    if (!elements.length) { event.preventDefault(); panel.value.focus({ preventScroll: true }); return }
    const active = document.activeElement
    if (event.shiftKey && (active === elements[0] || !panel.value.contains(active))) {
      event.preventDefault(); elements.at(-1)?.focus()
    } else if (!event.shiftKey && (active === elements.at(-1) || !panel.value.contains(active))) {
      event.preventDefault(); elements[0].focus()
    }
  }
  function handleOutside(event: MouseEvent) {
    if (!open.value || !isTopOverlay() || !options.closeOnOutside) return
    const target = event.target as Node
    if (!panel.value?.contains(target) && !options.trigger?.value?.contains(target)) options.closeOnOutside(event)
  }
  function unregister() {
    const stack = overlayStack()
    const index = stack.findIndex(entry => entry.id === id)
    if (index >= 0) stack.splice(index, 1)
    if (bound) {
      document.removeEventListener('keydown', handleKeydown, true)
      document.removeEventListener('click', handleOutside, true)
      bound = false
    }
  }
  function register() {
    if (typeof document === 'undefined') return
    restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const base = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue(options.zIndexToken ?? '--rr-z-dialog')) || 0
    const requested = options.zIndex?.()
    zIndex.value = requested ?? Math.max(base, (overlayStack().at(-1)?.zIndex ?? -1) + 1)
    overlayStack().push({ id, zIndex: zIndex.value })
    document.addEventListener('keydown', handleKeydown, true)
    document.addEventListener('click', handleOutside, true)
    bound = true
    return focusInitial()
  }
  async function release() {
    const wasTop = isTopOverlay()
    unregister()
    const target = restoreTarget
    restoreTarget = null
    await nextTick()
    if (wasTop && options.restoreFocus !== false && target?.isConnected && visible(target)) target.focus({ preventScroll: true })
  }
  watch(open, (value, oldValue) => {
    if (value && !oldValue) return register()
    if (!value && oldValue) return release()
  }, { immediate: true, flush: 'post' })
  if (parentOpen) watch(parentOpen, parent => { if (!parent && open.value) options.closeOnEscape?.() })
  onBeforeUnmount(() => { if (open.value) void release(); else unregister() })
  return { zIndex, isTopOverlay }
}
